import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig } from '../config/site';

const LAST_UPDATED = 'September 29, 2026';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen bg-white">
      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white"></div>
        <div className="relative max-w-3xl mx-auto px-6">
          <span className="text-sm font-semibold text-navy-600 uppercase tracking-wider">Legal</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-3 mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm">Last updated {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-3xl mx-auto px-6 space-y-10 text-slate-600 leading-relaxed">
          <div className="space-y-4">
            <p>
              This website and the services offered on it are operated by SW Capital LLC, doing business as
              Enhance Companies ("Enhance", "we", "us", or "our"). This Privacy Policy explains what
              information we collect when you visit enhancecompanies.com, apply to work with us, contact us,
              create an agent account, or purchase leads, dialer seats, or merchandise through our store, and
              how we use and protect that information.
            </p>
            <p>
              By using this website you agree to the practices described in this policy. If you do not agree,
              please do not use the site.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Information we collect</h2>
            <p>We collect information in three ways.</p>
            <p className="font-semibold text-slate-800">Information you give us</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Contact and application forms: your name, email address, phone number, state, licensing details, and anything you write in a message.</li>
              <li>Agent accounts: your name, email address, phone number, state, and a password, which is stored in hashed form and never visible to us.</li>
              <li>Purchases: the products you order, your billing name and address, and order history. Card details are entered directly with our payment processor and never touch our servers.</li>
              <li>Support and correspondence: emails, texts, and calls you exchange with our team.</li>
            </ul>
            <p className="font-semibold text-slate-800">Information collected automatically</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Device and usage data such as IP address, browser type, pages viewed, referring pages, and the date and time of your visit.</li>
              <li>Cookies and similar technologies used to keep you signed in, remember preferences, and measure site traffic.</li>
            </ul>
            <p className="font-semibold text-slate-800">Information from others</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Payment confirmations from our payment processor.</li>
              <li>Lead data delivered to your account when you purchase a lead product. That data belongs to the consumers described in it and is subject to the usage rules in your purchase terms.</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">How we use information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to applications, inquiries, and support requests.</li>
              <li>To create and manage agent accounts, process orders, and deliver purchased products.</li>
              <li>To send order confirmations, account notices, and service messages.</li>
              <li>To send marketing about our products and services, which you can opt out of at any time.</li>
              <li>To operate, secure, troubleshoot, and improve the website and our services.</li>
              <li>To comply with legal obligations and enforce our terms.</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">How we share information</h2>
            <p>We do not sell your personal information. We share it only in these cases.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Service providers who help us run the business, such as hosting, database, payment processing, email, texting, dialer, and analytics vendors. They may use your information only to provide services to us.</li>
              <li>Carriers and partners, when you apply to be contracted or ask us to connect you with one.</li>
              <li>Legal and safety reasons, when required by law, subpoena, or to protect the rights, property, or safety of Enhance, our agents, or others.</li>
              <li>Business transfers, if Enhance or SW Capital LLC is involved in a merger, acquisition, or sale of assets.</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Text messages and calls</h2>
            <p>
              If you provide a phone number and agree to be contacted, we or our service providers may call or
              text you about your application, account, orders, or offers. Message and data rates may apply.
              Reply STOP to any text to opt out of texts, or tell us during a call that you no longer wish to
              be contacted. Consent to texting is not a condition of purchase.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Cookies</h2>
            <p>
              We use cookies and local storage to keep you signed in, remember your settings, and understand
              how the site is used. You can block or delete cookies in your browser settings. Some parts of the
              site, including the agent dashboard and store checkout, will not work without them.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Data retention</h2>
            <p>
              We keep personal information for as long as needed to provide our services, meet legal,
              accounting, and compliance requirements, and resolve disputes. Agent account and order records
              are kept for the life of the account and for the period afterward required by law.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Security</h2>
            <p>
              We use industry standard safeguards, including encrypted connections, hashed passwords, and
              access controls, to protect your information. No method of transmission or storage is
              completely secure, so we cannot guarantee absolute security.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Your choices and rights</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You can update your account details from your agent profile at any time.</li>
              <li>You can unsubscribe from marketing emails using the link in any message, and from texts by replying STOP.</li>
              <li>You can ask us to access, correct, or delete the personal information we hold about you. Depending on where you live, you may have additional rights under state privacy laws such as the California Consumer Privacy Act. We will not discriminate against you for exercising them.</li>
            </ul>
            <p>To make a request, contact us using the details below. We may need to verify your identity before acting on it.</p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Children</h2>
            <p>
              Our services are intended for licensed insurance professionals and adults. We do not knowingly
              collect information from anyone under 18.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Changes to this policy</h2>
            <p>
              We may update this policy from time to time. The date at the top shows when it was last revised.
              Continued use of the site after a change means you accept the updated policy.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">Contact us</h2>
            <p>
              SW Capital LLC, DBA Enhance Companies
              <br />
              Phone:{' '}
              <a href={`tel:${siteConfig.contact.phone}`} className="text-navy-600 hover:text-navy-700 underline">
                {siteConfig.contact.phone}
              </a>
              <br />
              Online:{' '}
              <Link to="/contact" className="text-navy-600 hover:text-navy-700 underline">
                contact form
              </Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
